import { useEffect, useRef, useState } from 'react';
import { useLocalization } from 'libs.localization.react';
export const useSourceTranslations = (sourceAsset) => {
    const { localization, error } = useLocalization(sourceAsset);
    const [sourceMap, setSourceMap] = useState(new Map());
    const [isLoading, setIsLoading] = useState(false);
    const [translationError, setTranslationError] = useState(null);
    const [localizationDocument, setLocalizationDocument] = useState(null);
    const fetchedAssetRef = useRef(null);
    useEffect(() => {
        if (!localization || !sourceAsset) {
            return;
        }
        // Create a stable identifier for the asset
        const assetId = typeof sourceAsset === 'string'
            ? sourceAsset
            : 'type' in sourceAsset
                ? `${sourceAsset.type}-${sourceAsset.id}-${sourceAsset.version}`
                : `${sourceAsset.activity.identifier}-${sourceAsset.activity.version}`;
        // Skip if we've already fetched for this asset
        if (fetchedAssetRef.current === assetId) {
            return;
        }
        const fetchSourceTranslations = async () => {
            setIsLoading(true);
            setTranslationError(null);
            fetchedAssetRef.current = assetId;
            try {
                const doc = await localization.getDatabaseEntry();
                if (!doc) {
                    throw new Error('Localization document not found');
                }
                setLocalizationDocument(doc);
                const baseLocale = doc.base_locale;
                const supportedLocales = Object.keys(doc.supported_locales || {});
                const filteredLocales = supportedLocales.filter((locale) => locale !== baseLocale);
                // Use Promise.all to handle async operations properly
                const translationPromises = filteredLocales.map(async (locale) => {
                    const _history = await localization?.getLocaleHistory(locale);
                    const sortedHistory = _history?.sort((a, b) => (a?.version ?? 0) - (b?.version ?? 0)); // sorted numeric ascending
                    const history = sortedHistory?.map((h) => ({
                        version: h.version ?? 0,
                        status: h.status ?? 'unknown',
                    })) || [];
                    return { locale, history };
                });
                const results = await Promise.all(translationPromises);
                // Update sourceMap with all results at once
                const newSourceMap = new Map();
                results.forEach(({ locale, history }) => {
                    newSourceMap.set(locale, history);
                });
                setSourceMap(newSourceMap);
            }
            catch (err) {
                setTranslationError(err instanceof Error ? err : new Error('Failed to fetch source translations'));
                fetchedAssetRef.current = null; // Reset on error so we can retry
            }
            finally {
                setIsLoading(false);
            }
        };
        fetchSourceTranslations();
    }, [sourceAsset, localization]); // Include both dependencies but use ref to prevent duplicate calls
    return {
        localization,
        error: error || translationError,
        sourceMap,
        isLoading,
        localizationDocument,
    };
};
